import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { DocumentAccessLevel, DocumentStatus, FileType } from '@alucio/aws-beacon-amplify/src/models';
import { RootState } from 'src/state/redux';
import { CalloutState } from 'src/state/redux/slice/contentPreviewModal';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useContent } from 'src/state/context/ContentProvider/ContentProvider';
import { isCustomDeckORM, isFolderItemORM } from 'src/types/typeguards';
import { useMeetingPresentedMeta } from 'src/components/ContentPreviewModalV2/State/MeetingPresentedMetaProvider';
import { useContentPreviewModal } from 'src/components/ContentPreviewModalV2/State/ContentPreviewModalStateProvider';
import { useDNADocumentActions } from 'src/components/DNA/Document/DNADocument.actions';
import { useDNADocumentVersionActions } from 'src/components/DNA/Document/DNADocumentVersion.actions';
import { useDNACustomDeckActions } from 'src/components/DNA/Document/DNACustomDeck.actions';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';

export enum SidebarOptionsEnum {
  speakerNotes = 'speakerNotes',
  customNotes = 'customNotes',
  meetingNotes = 'meetingNotes',
  versionHistory = 'versionHistory',
  associatedFiles = 'associatedFiles',
  relatedSlides = 'relatedSlides',
  details = 'details',
  textInsertion = 'textInsertion',
  callouts = 'callouts',
}
export type SidebarOptions = keyof typeof SidebarOptionsEnum;

type OptionAvailabilityConfigs = Record<SidebarOptions, boolean>

type SidebarContextType = {
  availableSidebarOptions: SidebarOptions[],
  selectedSideBarOption: SidebarOptions | null,
  setSelectedSideBarOption: React.Dispatch<React.SetStateAction<SidebarOptions | null>>,
  allowShareOrDownload: boolean,
  allowRelatedSlidesProvider: boolean,
  displayShareButton: boolean,
  disPlayDownloadButton: boolean,
  toggleSidebar: () => void,
  onShare: () => void,
  onDownload: () => void,
  calloutState: CalloutState,
}

const CALLOUTS_AVAILABLE_DOC_TYPE = ['PDF', 'PPTX'];
const RELATED_SLIDES_AVAILABLE_DOC_TYPE = ['PDF', 'PPTX'];

/** Ensure we are only allowing a single instance of each option */
const SIDEBAR_OPTION_ORDER = [
  SidebarOptionsEnum.speakerNotes,
  SidebarOptionsEnum.customNotes,
  SidebarOptionsEnum.meetingNotes,
  SidebarOptionsEnum.versionHistory,
  SidebarOptionsEnum.associatedFiles,
  SidebarOptionsEnum.relatedSlides,
  SidebarOptionsEnum.details,
  SidebarOptionsEnum.textInsertion,
  SidebarOptionsEnum.callouts,
]

const SidebarContext = React.createContext<SidebarContextType>(undefined!);
SidebarContext.displayName = 'SidebarContext';

const SidebarProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props;
  const { isOnline } = useAppSettings();
  const { activePresentation } = useContent();
  const { onOpenMeetingNotes } = useMeetingPresentedMeta();
  const documentActions = useDNADocumentActions();
  const { shareEmail } = useDNADocumentVersionActions();
  const { shareEmail: shareCustomDeckEmail } = useDNACustomDeckActions();
  const {
    isFolderItem,
    activeDocumentVersionORM,
    isCustomDeckPreviewMode,
    isLibrary,
    isSearchResults,
    isDashBoard,
    isBookmarks,
    isPublisher,
    canCurrentSlideInsertText,
    isModifiedDocumentVersion,
    isAssociatedFile,
  } = useContentPreviewModal();
  const featureFlags = useFeatureFlags(
    'enableShareCustomDeck',
    'enableMeetingNotes',
    'enableCustomNotes',
    'BEAC_6985_related_slides',
    'BEAC_8431_allow_in_progress_related_slides',
  );
  const {
    meetingId,
    hubId,
    openedTab,
    calloutState,
  } = useSelector((state: RootState) => state.contentPreviewModal);
  const openMeetingNotebyDefault = meetingId && featureFlags.enableMeetingNotes;
  const [selectedSideBarOption, setSelectedSideBarOption] = useState<SidebarOptions | null>(
    (openMeetingNotebyDefault && SidebarOptionsEnum.meetingNotes) || null);

  const isUserDocument = activeDocumentVersionORM?.relations.documentORM.model.accessLevel === DocumentAccessLevel.USER;
  const isUserDocumentOrFolder = (isFolderItem && !activeDocumentVersionORM) || isUserDocument;
  const isValidDocTypeForCallouts = CALLOUTS_AVAILABLE_DOC_TYPE.includes(activeDocumentVersionORM?.model.type || '');
  const isValidDocTypeForRelatedSlides =
    RELATED_SLIDES_AVAILABLE_DOC_TYPE.includes(activeDocumentVersionORM?.model.type || '');
  const hasAssociatedFiles = (activeDocumentVersionORM?.model.associatedFiles?.length || 0) > 0 && !isAssociatedFile;
  const hasPermissionToCallout = activeDocumentVersionORM?.meta.permissions.MSLExternalNotatable;
  const isShareableDeck = isFolderItemORM(activePresentation?.presentable.orm) &&
    isCustomDeckORM(activePresentation?.presentable.orm.relations.itemORM) &&
    featureFlags.enableShareCustomDeck &&
    activePresentation?.presentable.orm.relations.itemORM.meta.permissions.MSLShare
  const isLatestPublished = activeDocumentVersionORM?.meta.version.isLatestPublished;
  const isNotRevoked = activeDocumentVersionORM?.meta.sealedStatus !== DocumentStatus.REVOKED;
  const isNotArchived = activeDocumentVersionORM?.meta.sealedStatus !== DocumentStatus.ARCHIVED;
  const MSLShare = activeDocumentVersionORM?.relations.documentORM
    .relations.version.latestPublishedDocumentVersionORM?.meta.permissions.MSLShare;
  const allowShareOrDownload = !!(isOnline &&
    ((activeDocumentVersionORM && !isModifiedDocumentVersion && !hubId) || isShareableDeck));
  const displayShareButton = (isNotRevoked && isLatestPublished && MSLShare) ||
  !!isShareableDeck;
  const disPlayDownloadButton = !!activeDocumentVersionORM &&
    (activeDocumentVersionORM?.model.type !== FileType.WEB) &&
    (isPublisher || (!!isLatestPublished && activeDocumentVersionORM?.meta.permissions.MSLDownload));

  const allowRelatedSlidesProvider = useMemo(() => {
    return featureFlags.BEAC_6985_related_slides &&
      (isLibrary || isSearchResults || isDashBoard || isBookmarks) &&
      isNotRevoked &&
      isNotArchived &&
      isValidDocTypeForRelatedSlides;
  }, [
    featureFlags.BEAC_6985_related_slides,
    isLibrary,
    isSearchResults,
    isDashBoard,
    isBookmarks,
    isNotRevoked,
    isNotArchived,
    isValidDocTypeForRelatedSlides,
  ])

  const displayCustomNotes = useMemo(() => {
    return featureFlags.enableCustomNotes && !hubId && !isUserDocument && !isPublisher;
  }, [featureFlags.enableCustomNotes, hubId, isUserDocument, isPublisher])

  const displayMeetingNotes = useMemo(() => {
    return featureFlags.enableMeetingNotes && !!meetingId;
  }, [featureFlags.enableMeetingNotes, meetingId])

  const displayVersionHistory = useMemo(() => {
    return !isUserDocumentOrFolder && !hubId;
  }, [isUserDocumentOrFolder, hubId])

  const displayAssociatedFiles = useMemo(() => {
    return !isUserDocumentOrFolder && hasAssociatedFiles && !hubId;
  }, [isUserDocumentOrFolder, hasAssociatedFiles, hubId])

  const displayRelatedSlides = useMemo(() => {
    const allowInProgressRelatedSlides = isPublisher &&
      featureFlags.BEAC_8431_allow_in_progress_related_slides;

    return allowRelatedSlidesProvider &&
      (!!isLatestPublished || allowInProgressRelatedSlides) &&
      !isAssociatedFile;
  }, [
    allowRelatedSlidesProvider,
    isLatestPublished,
    isPublisher,
    featureFlags.BEAC_8431_allow_in_progress_related_slides,
    isAssociatedFile,
  ])

  const displayDetails = useMemo(() => {
    return !isUserDocumentOrFolder && !hubId;
  }, [isUserDocumentOrFolder, hubId])

  const displayTextInsertion = useMemo(() => {
    return !isCustomDeckPreviewMode && !!canCurrentSlideInsertText && !isPublisher;
  }, [canCurrentSlideInsertText, isPublisher, isCustomDeckPreviewMode])

  const displayCallout = useMemo(() => {
    return !!hubId && isValidDocTypeForCallouts && !!hasPermissionToCallout;
  }, [hubId, isValidDocTypeForCallouts, hasPermissionToCallout, calloutState]);

  /** Mapping of visibility conditions for each option */
  const optionAvailabilityConfigs: OptionAvailabilityConfigs = useMemo(() => {
    return {
      speakerNotes: true,
      customNotes: displayCustomNotes,
      meetingNotes: displayMeetingNotes,
      versionHistory: displayVersionHistory,
      associatedFiles: displayAssociatedFiles,
      relatedSlides: displayRelatedSlides,
      details: displayDetails,
      textInsertion: displayTextInsertion,
      callouts: displayCallout,
    }
  }, [
    displayCustomNotes,
    displayMeetingNotes,
    displayVersionHistory,
    displayAssociatedFiles,
    displayRelatedSlides,
    displayDetails,
    displayTextInsertion,
    displayCallout,
  ])

  const availableSidebarOptions = useMemo(() => {
    const options: SidebarOptions[] = [];
    SIDEBAR_OPTION_ORDER.forEach(option => {
      if (optionAvailabilityConfigs[option]) {
        options.push(option);
      }
    });
    return options;
  }, [optionAvailabilityConfigs]);

  /* USEEFFECT START */
  useEffect(() => {
    if (openedTab === SidebarOptionsEnum.textInsertion && canCurrentSlideInsertText) {
      setSelectedSideBarOption(openedTab)
    } else if (openedTab === SidebarOptionsEnum.callouts && displayCallout) {
      setSelectedSideBarOption(openedTab)
    } else if (openedTab) {
      setSelectedSideBarOption(openedTab)
    }

    return () => {
      setSelectedSideBarOption(null)
    }
  }, [openedTab, canCurrentSlideInsertText, displayCallout]);

  useEffect(() => {
    // CLOSE SIDEBAR IF SWITCHING TO VIEW ASSICUATED FILE
    if (isAssociatedFile) setSelectedSideBarOption(null)
  }, [isAssociatedFile]);

  useEffect(() => {
    if (selectedSideBarOption === SidebarOptionsEnum.meetingNotes) onOpenMeetingNotes();
    // CLOSE SIDEBAR IF TEXT INSERTION TAB IS OPEN BUT THE SLIDE NAVIGATES TO DOES NOT ALLOW TEXT INSERTION
    if (selectedSideBarOption === SidebarOptionsEnum.textInsertion && !canCurrentSlideInsertText) {
      setSelectedSideBarOption(null);
    }
    // CLOSE SIDEBAR IF RELATED SLIDES TAB IS OPEN BUT NAVIGATES TO DOCUMENT (VERSION) THAT DOES NOT ALLOW THIS FEATURE
    if (selectedSideBarOption === SidebarOptionsEnum.relatedSlides && !displayRelatedSlides) {
      setSelectedSideBarOption(null);
    }
  }, [selectedSideBarOption, onOpenMeetingNotes, canCurrentSlideInsertText, displayRelatedSlides]);
  /* USEEFFECT END */

  /* FUNCTION START */
  const toggleSidebar = useCallback(() => {
    selectedSideBarOption ? setSelectedSideBarOption(null) : setSelectedSideBarOption(availableSidebarOptions[0]);
  }, [selectedSideBarOption, availableSidebarOptions]);

  const onShare = useCallback(() => {
    if (activeDocumentVersionORM) shareEmail(activeDocumentVersionORM, undefined, meetingId)();
    else if (
      isFolderItemORM(activePresentation?.presentable.orm) &&
      isCustomDeckORM(activePresentation?.presentable.orm.relations.itemORM) &&
      activePresentation?.presentable.orm.relations.itemORM
    ) {
      shareCustomDeckEmail(
        activePresentation.presentable.orm.relations.itemORM,
        activePresentation.presentable.orm, meetingId,
      )()
    }
  }, [activeDocumentVersionORM, shareEmail, activePresentation, shareCustomDeckEmail])

  const onDownload = useCallback(() => {
    if (activeDocumentVersionORM) documentActions.download(activeDocumentVersionORM.relations.documentORM)();
  }, [activeDocumentVersionORM, documentActions]);
  /* FUNCTION END */

  const contextValue: SidebarContextType = {
    availableSidebarOptions,
    selectedSideBarOption,
    setSelectedSideBarOption,
    allowShareOrDownload,
    allowRelatedSlidesProvider,
    displayShareButton,
    disPlayDownloadButton,
    toggleSidebar,
    onShare,
    onDownload,
    calloutState: calloutState || CalloutState.NORMAL,
  }

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  )
}

SidebarProvider.displayName = 'SidebarProvider';

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within the SidebarProvider');
  }
  return context;
};

export default SidebarProvider;
